import { type PropsWithChildren, useMemo } from 'react';
import { assertDefined } from '@epihub/core/src/fn/assert';
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import type { UseGlobalStoreWithSlice } from '@epihub/web/src/zustand/types';
import type { ErrorBoundaryProps } from '@epihub/web/src/components/ErrorBoundary';
import { ErrorBoundary } from '@epihub/web/src/components/ErrorBoundary';
import { expectMobileDeps } from '@epihub/web/src/_ctx/deps';
import { themeStateVersioner, themeVersioner } from '../types';
import { type ThemeStoreSlice } from '../zustand';
import type { EmotionThemeType } from '../emotion';
import { createMobileSnackbarProvider } from './createMobileSnackbarProvider';

type ProviderProps = {
  useGlobalStore: UseGlobalStoreWithSlice<ThemeStoreSlice>;
  errorBoundaryProps: ErrorBoundaryProps;
};

export function createMobileProvider() {
  const TamaguiProvider = assertDefined(
    expectMobileDeps().tamagui?.TamaguiProvider,
    'tamagui import was not passed as an injectable dependency',
  );
  const createTamagui = assertDefined(
    expectMobileDeps().tamagui?.createTamagui,
    'tamagui import was not passed as an injectable dependency',
  );

  const TamaguiSnackbarProvider = createMobileSnackbarProvider();

  return ({ children, useGlobalStore, errorBoundaryProps }: PropsWithChildren<ProviderProps>) => {
    const theme = useGlobalStore((store) => store.theme.theme);
    const state = useGlobalStore((store) => store.theme.__state);
    const updateTheme = useGlobalStore((store) => store.theme.updateTheme);

    const versionedTheme = useMemo(
      () => {
        return {
          ...theme,
          v0: themeVersioner('v0', theme),
          updateTheme: updateTheme as EmotionThemeType['updateTheme'],
        } satisfies EmotionThemeType;
      },
      [theme, updateTheme],
    );

    const stateV0 = useMemo(
      () => themeStateVersioner('v0', state),
      [state],
    );

    const config = useMemo(() => createTamagui(versionedTheme.v0.tamagui), [versionedTheme.v0.tamagui]);
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <EmotionThemeProvider theme={versionedTheme}>
          <TamaguiProvider config={config}>
            <TamaguiSnackbarProvider>
              {children}
            </TamaguiSnackbarProvider>
          </TamaguiProvider>
        </EmotionThemeProvider>
      </ErrorBoundary>
    );
  };
}
